import { Link } from "react-router-dom";
import { Facebook, Instagram, Linkedin, MapPin, Phone, Mail } from "lucide-react";
import { ADDRESS, PHONE_DISPLAY, ALT_PHONE_DISPLAY, EMAIL, SOCIALS, WHATSAPP_LINK, STACKCP_URL } from "@/data/site";

const TikTokIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64c.3 0 .6.05.88.13V9.4a6.33 6.33 0 0 0-1-.05A6.34 6.34 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1.04-.1z" />
  </svg>
);

const XIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M18.9 1.15h3.68l-8.04 9.19L24 22.85h-7.41l-5.8-7.58-6.64 7.58H.47l8.6-9.83L0 1.15h7.59l5.24 6.93zm-1.29 19.5h2.04L6.49 3.24H4.3z" />
  </svg>
);

const SOCIAL_ICONS = {
  facebook: Facebook,
  instagram: Instagram,
  tiktok: TikTokIcon,
  x: XIcon,
  linkedin: Linkedin,
};

const Footer = () => (
  <footer className="relative overflow-hidden border-t border-white/10 bg-[#070708]" data-testid="footer">
    <div className="pointer-events-none absolute inset-x-0 bottom-0 select-none overflow-hidden">
      <div className="text-stroke font-display whitespace-nowrap text-center text-[18vw] font-extrabold leading-[0.85] tracking-tight opacity-60">
        SPARK PLUG
      </div>
    </div>
    <div className="relative z-10 mx-auto max-w-7xl px-4 pb-56 pt-20 sm:px-8 lg:px-12">
      <div className="grid gap-12 md:grid-cols-2 lg:grid-cols-4">
        <div>
          <div className="inline-flex items-center">
            <img src="/assets/sparkplug-logo.webp" alt="Spark Plug" className="h-12 w-auto" />
          </div>
          <p className="mt-5 max-w-xs text-sm leading-relaxed text-[#A1A1AA]">
            Digital marketing, websites, apps, domains and hosting — one team in Fort Lauderdale plugging your business into growth.
          </p>
          <div className="mt-6 flex gap-3">
            {SOCIALS.map((s) => {
              const Icon = SOCIAL_ICONS[s.id];
              return (
                <a
                  key={s.id}
                  href={s.href}
                  aria-label={s.label}
                  data-testid={`social-link-${s.id}`}
                  className="flex h-10 w-10 items-center justify-center rounded-full border border-white/10 text-[#A1A1AA] transition-all duration-300 hover:border-[#D9A92E]/60 hover:text-[#D9A92E]"
                >
                  <Icon className="h-4 w-4" />
                </a>
              );
            })}
          </div>
        </div>
        <div>
          <h4 className="mono text-xs uppercase tracking-[0.2em] text-[#E9C766]">Services</h4>
          <ul className="mt-5 space-y-3 text-sm text-[#A1A1AA]">
            <li><Link to="/#services" className="transition-colors hover:text-white">Digital Marketing</Link></li>
            <li><Link to="/#services" className="transition-colors hover:text-white">Website Development</Link></li>
            <li><Link to="/#services" className="transition-colors hover:text-white">App Development</Link></li>
            <li><Link to="/#hosting" className="transition-colors hover:text-white">Shared Hosting</Link></li>
            <li><Link to="/#hosting" className="transition-colors hover:text-white">Domain Registration</Link></li>
            <li><Link to="/#all-in-one" className="transition-colors hover:text-white">All-In-One Package</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="mono text-xs uppercase tracking-[0.2em] text-[#E9C766]">Company</h4>
          <ul className="mt-5 space-y-3 text-sm text-[#A1A1AA]">
            <li><Link to="/#portfolio" className="transition-colors hover:text-white">Portfolio</Link></li>
            <li><Link to="/#why-us" className="transition-colors hover:text-white">Why Choose Us</Link></li>
            <li><Link to="/blog" className="transition-colors hover:text-white">Blog</Link></li>
            <li><Link to="/website-development-form" data-testid="footer-website-brief-link" className="text-[#E9C766] transition-colors hover:text-white">Website Development Form</Link></li>
            <li><Link to="/contact" className="transition-colors hover:text-white">Contact</Link></li>
            <li><a href={STACKCP_URL} target="_blank" rel="noopener noreferrer" data-testid="footer-my-account-link" className="transition-colors hover:text-white">Client Login (My Account)</a></li>
            <li><Link to="/privacy-policy" className="transition-colors hover:text-white">Privacy Policy</Link></li>
            <li><Link to="/terms" className="transition-colors hover:text-white">Terms &amp; Conditions</Link></li>
            <li><Link to="/refund-policy" className="transition-colors hover:text-white">Refund Policy</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="mono text-xs uppercase tracking-[0.2em] text-[#E9C766]">Get In Touch</h4>
          <ul className="mt-5 space-y-4 text-sm text-[#A1A1AA]">
            <li className="flex gap-3">
              <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-[#D9A92E]" />
              <span data-testid="footer-address">{ADDRESS}</span>
            </li>
            <li className="flex gap-3">
              <Phone className="mt-0.5 h-4 w-4 shrink-0 text-[#D9A92E]" />
              <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" className="transition-colors hover:text-white">
                {PHONE_DISPLAY}
              </a>
            </li>
            <li className="flex gap-3">
              <Phone className="mt-0.5 h-4 w-4 shrink-0 text-[#D9A92E]" />
              <a href="tel:+17547551717" className="transition-colors hover:text-white">
                {ALT_PHONE_DISPLAY}
              </a>
            </li>
            <li className="flex gap-3">
              <Mail className="mt-0.5 h-4 w-4 shrink-0 text-[#D9A92E]" />
              <span>{EMAIL}</span>
            </li>
          </ul>
        </div>
      </div>
      <div className="mt-16 flex flex-col items-center justify-between gap-4 border-t border-white/10 pt-8 sm:flex-row">
        <p className="text-xs text-[#6B7280]">© 2026 Spark Plug. All rights reserved.</p>
        <p className="mono text-xs uppercase tracking-[0.2em] text-[#6B7280]">Ignite your digital growth</p>
      </div>
    </div>
  </footer>
);

export default Footer;
